import { useMemo, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { ArrowDown, ArrowUp, ArrowUpDown, Download } from 'lucide-react';
import { toCsv } from '@tradeoutlet/shared';
import { Button, ErrorBox, Spinner } from './index';

/**
 * UI review: one table for lists and reports. Sortable headers (aria-sort), sticky header, loading /
 * error / empty rows, optional row selection and CSV export of what is shown (in the shown order).
 */
export interface Column<T> {
  key: string;
  header: ReactNode;
  /** Plain value used to sort and for CSV. Columns without one are not sortable. */
  value?: (row: T) => string | number | null | undefined;
  /** Rendered cell; defaults to the value. */
  cell?: (row: T) => ReactNode;
  /** Numbers line up on the right. */
  align?: 'left' | 'right';
  /** CSV header when the header is not plain text. */
  csvHeader?: string;
  className?: string;
}

type Sort = { key: string; dir: 'asc' | 'desc' } | null;

export function sortRows<T>(rows: T[], columns: Column<T>[], sort: Sort): T[] {
  const col = sort && columns.find(c => c.key === sort.key);
  if (!col?.value) return rows;
  const v = col.value;
  const mul = sort!.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = v(a), y = v(b);
    // Blanks always sink to the bottom, whichever way the column is sorted.
    if (x == null || x === '') return y == null || y === '' ? 0 : 1;
    if (y == null || y === '') return -1;
    return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), 'en-GB', { numeric: true, sensitivity: 'base' })) * mul;
  });
}

export function DataTable<T>({ rows, columns, rowKey, loading, error, onRetry, empty = 'Nothing to show', csv, selected, onSelect, onRowClick,
  initialSort = null, maxHeight, caption, className, inset }: {
  rows: T[] | undefined; columns: Column<T>[]; rowKey: (row: T) => string;
  loading?: boolean; error?: unknown; onRetry?: () => void; empty?: ReactNode;
  /** File name (without .csv) to offer a download of the shown rows. */
  csv?: string;
  selected?: Set<string>; onSelect?: (next: Set<string>) => void;
  onRowClick?: (row: T) => void;
  initialSort?: Sort; maxHeight?: string; caption?: string; className?: string;
  /** Card edge to edge (inside a -m-4 card body): first and last cells get the card's 1rem padding, as list rows do. */
  inset?: boolean;
}) {
  const [sort, setSort] = useState<Sort>(initialSort);
  const shown = useMemo(() => sortRows(rows ?? [], columns, sort), [rows, columns, sort]);
  const span = columns.length + (onSelect ? 1 : 0);
  const allOn = Boolean(onSelect && shown.length && shown.every(r => selected?.has(rowKey(r))));
  const edge = inset && 'first:pl-4 last:pr-4';
  const toggle = (k: string) => { const n = new Set(selected); if (n.has(k)) n.delete(k); else n.add(k); onSelect?.(n); };
  const download = () => {
    const cols = columns.filter(c => c.value).map(c => ({ key: c.key, header: c.csvHeader ?? (typeof c.header === 'string' ? c.header : c.key) }));
    const data = shown.map(r => Object.fromEntries(columns.filter(c => c.value).map(c => [c.key, c.value!(r) ?? ''])));
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['\uFEFF' + toCsv(data, cols)], { type: 'text/csv;charset=utf-8' }));
    a.download = `${csv}.csv`; a.click(); URL.revokeObjectURL(a.href);
  };
  const cycle = (key: string) => setSort(s => !s || s.key !== key ? { key, dir: 'asc' } : s.dir === 'asc' ? { key, dir: 'desc' } : null);
  return (
    <div className={clsx('min-w-0', className)}>
      {csv && rows && rows.length > 0 && (
        <div className="mb-2 flex justify-end"><Button size="sm" variant="ghost" onClick={download}><Download className="h-3.5 w-3.5" />CSV</Button></div>
      )}
      <div className="scroll-thin overflow-auto" style={maxHeight ? { maxHeight } : undefined}>
        <table className="w-full text-sm">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead className="sticky top-0 z-10 bg-white text-left text-xs uppercase text-slate-500 dark:bg-slate-900">
            <tr>
              {onSelect && <th className={clsx('w-8 py-2', edge)}><input type="checkbox" aria-label="Select all shown" checked={allOn}
                onChange={() => onSelect(allOn ? new Set() : new Set(shown.map(rowKey)))} /></th>}
              {columns.map(c => {
                const dir = sort?.key === c.key ? sort.dir : null;
                return (
                  <th key={c.key} scope="col" aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : undefined}
                    className={clsx('whitespace-nowrap py-2 pr-3 font-semibold', edge, c.align === 'right' && 'text-right', c.className)}>
                    {c.value ? (
                      <button type="button" onClick={() => cycle(c.key)} className={clsx('inline-flex items-center gap-1 uppercase hover:text-slate-900 dark:hover:text-white', c.align === 'right' && 'flex-row-reverse')}>
                        {c.header}{dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : dir === 'desc' ? <ArrowDown className="h-3 w-3" /> : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                      </button>
                    ) : c.header}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {error ? <tr><td colSpan={span} className={clsx('py-3', inset && 'px-4')}><ErrorBox error={error} onRetry={onRetry} /></td></tr>
              : loading && !rows ? <tr><td colSpan={span} className={clsx('py-6', inset && 'px-4')}><Spinner /></td></tr>
              : !shown.length ? <tr><td colSpan={span} className={clsx('py-6 text-center text-sm text-slate-500', inset && 'px-4')}>{empty}</td></tr>
              : shown.map(r => {
                const k = rowKey(r);
                return (
                  <tr key={k} onClick={onRowClick ? () => onRowClick(r) : undefined}
                    className={clsx('border-t border-slate-100 dark:border-white/5', onRowClick && 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50', selected?.has(k) && 'bg-slate-50 dark:bg-slate-800/50')}>
                    {onSelect && <td className={clsx(inset ? 'py-2.5' : 'py-1.5', edge)} onClick={e => e.stopPropagation()}><input type="checkbox" aria-label="Select row" checked={Boolean(selected?.has(k))} onChange={() => toggle(k)} /></td>}
                    {columns.map(c => (
                      <td key={c.key} className={clsx(inset ? 'py-2.5 pr-3' : 'py-1.5 pr-3', edge, c.align === 'right' && 'text-right tabular-nums', c.className)}>
                        {c.cell ? c.cell(r) : (c.value?.(r) ?? '–')}
                      </td>
                    ))}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
